import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { formatRupiah, todayWITA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";
import { Plus, Trash2 } from "lucide-react";

export function TotalanDialog({ supplierId, invoices, onClose, onSaved }) {
  const [credits, setCredits] = useState({ credits: {}, available: 0 });
  const [selectedReturns, setSelectedReturns] = useState([]);
  const [tranches, setTranches] = useState([{ amount: "", method: "giro", cair_date: todayWITA(), warkat_number: "", warkat_bank: "" }]);

  useEffect(() => { api.get(`/returns/credits?supplier_id=${supplierId}`).then((r) => setCredits(r.data)); }, [supplierId]);

  const availableReturns = Object.entries(credits.credits).filter(([, v]) => v.available > 0);

  const allocs = useMemo(() => {
    const result = [];
    const cap = {};
    invoices.forEach((i) => { cap[i.id] = i.total_amount; });
    selectedReturns.forEach((rid) => {
      const v = credits.credits[rid];
      if (!v) return;
      let remaining = v.available;
      for (const inv of invoices) {
        if (remaining <= 0.001) break;
        const take = Math.min(remaining, cap[inv.id]);
        if (take > 0.001) {
          result.push({ return_id: rid, invoice_id: inv.id, amount: Math.round(take * 100) / 100 });
          cap[inv.id] -= take;
          remaining -= take;
        }
      }
    });
    return result;
  }, [selectedReturns, invoices, credits]);

  const creditUsed = allocs.reduce((a, x) => a + x.amount, 0);
  const gross = invoices.reduce((a, i) => a + i.total_amount, 0);
  const netTotal = gross - creditUsed;
  const trSum = tranches.reduce((a, t) => a + (Number(t.amount) || 0), 0);

  const submit = async () => {
    const trs = tranches.filter((t) => Number(t.amount) > 0);
    if (trs.length === 0) return toast.error("Isi minimal satu termin pembayaran");
    if (Math.abs(trs.reduce((a, t) => a + Number(t.amount), 0) - netTotal) > 1) return toast.error(`Total termin (${formatRupiah(trSum)}) harus sama dengan pembayaran bersih (${formatRupiah(netTotal)})`);
    for (const t of trs) {
      if (t.method === "giro" && (!t.warkat_number || !t.warkat_bank)) return toast.error("Setiap BG wajib nomor warkat & bank");
      if (!t.cair_date) return toast.error("Tanggal cair tiap termin wajib diisi");
    }
    try {
      await api.post("/schedules", {
        supplier_id: supplierId, invoice_ids: invoices.map((i) => i.id), allocations: allocs,
        method: "split",
        tranches: trs.map((t) => ({ amount: Number(t.amount), method: t.method, cair_date: t.cair_date, warkat_number: t.method === "giro" ? t.warkat_number : null, warkat_bank: t.method === "giro" ? t.warkat_bank : null })),
      });
      toast.success("Totalan berhasil dibuat");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Jadikan Totalan · {invoices.length} nota</DialogTitle><DialogDescription className="sr-only">Gabungkan nota terpilih menjadi satu totalan dengan beberapa termin BG</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="bg-slate-50 rounded p-2 space-y-1 max-h-28 overflow-y-auto" data-testid="totalan-invoice-list">
            {invoices.map((i) => (
              <div key={i.id} className="flex justify-between text-xs">
                <span className="font-medium">{i.invoice_number}</span>
                <span className="font-mono">{formatRupiah(i.total_amount)}</span>
              </div>
            ))}
            <div className="flex justify-between text-xs font-semibold border-t border-slate-200 pt-1">
              <span>Total</span><span className="font-mono">{formatRupiah(gross)}</span>
            </div>
          </div>

          {availableReturns.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Nota Retur (potong otomatis)</Label>
              {availableReturns.map(([rid, v]) => (
                <label key={rid} className="flex items-center gap-2 bg-slate-50 p-2 rounded cursor-pointer" data-testid={`totalan-return-${v.code}`}>
                  <Checkbox checked={selectedReturns.includes(rid)} onCheckedChange={() => setSelectedReturns(selectedReturns.includes(rid) ? selectedReturns.filter((x) => x !== rid) : [...selectedReturns, rid])} data-testid={`totalan-return-check-${v.code}`} />
                  <div className="flex-1 flex justify-between text-xs">
                    <span className="font-mono">{v.code}</span>
                    <span className="font-semibold text-emerald-600">− {formatRupiah(v.available)}</span>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Termin Pembayaran (BG)</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setTranches([...tranches, { amount: "", method: "giro", cair_date: todayWITA(), warkat_number: "", warkat_bank: "" }])} data-testid="totalan-add-tranche"><Plus className="h-3 w-3" /></Button>
            </div>
            {tranches.map((t, idx) => (
              <div key={idx} className="bg-slate-50 p-2 rounded space-y-1" data-testid={`totalan-tranche-${idx}`}>
                <div className="flex gap-1">
                  <Input type="number" placeholder="Nominal" className="h-8 text-xs" value={t.amount} onChange={(e) => setTranches(tranches.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} data-testid={`totalan-tranche-amount-${idx}`} />
                  <Select value={t.method} onValueChange={(v) => setTranches(tranches.map((x, i) => i === idx ? { ...x, method: v } : x))}>
                    <SelectTrigger className="h-8 text-xs w-24" data-testid={`totalan-tranche-method-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="giro">Giro</SelectItem><SelectItem value="transfer">Transfer</SelectItem></SelectContent>
                  </Select>
                  {tranches.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setTranches(tranches.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>}
                </div>
                <DatePicker value={t.cair_date} onChange={(v) => setTranches(tranches.map((x, i) => i === idx ? { ...x, cair_date: v } : x))} testid={`totalan-tranche-date-${idx}`} />
                {t.method === "giro" && (
                  <div className="flex gap-1">
                    <Input placeholder="No. Warkat" className="h-8 text-xs" value={t.warkat_number} onChange={(e) => setTranches(tranches.map((x, i) => i === idx ? { ...x, warkat_number: e.target.value } : x))} data-testid={`totalan-tranche-warkat-${idx}`} />
                    <Input placeholder="Bank" className="h-8 text-xs" value={t.warkat_bank} onChange={(e) => setTranches(tranches.map((x, i) => i === idx ? { ...x, warkat_bank: e.target.value } : x))} data-testid={`totalan-tranche-bank-${idx}`} />
                  </div>
                )}
              </div>
            ))}
            <div className="flex justify-between text-xs px-1">
              <span className="text-slate-500">Total termin</span>
              <span className={`font-mono font-semibold ${Math.abs(trSum - netTotal) > 1 ? "text-rose-600" : "text-emerald-600"}`} data-testid="totalan-tranche-sum">{formatRupiah(trSum)}</span>
            </div>
          </div>

          <div className="total-box rounded-2xl p-4 text-right">
            <div className="text-xs text-slate-300">Pembayaran Bersih</div>
            <div className="font-mono font-bold text-lg" data-testid="totalan-net-amount">{formatRupiah(netTotal)}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="totalan-cancel">Batal</Button>
          <Button onClick={submit} data-testid="totalan-submit">Simpan Totalan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
